/**
 * Validation utilities for form inputs
 */
import { parseLocalDate } from './dates';

export interface ValidationResult {
    isValid: boolean;
    error?: string;
}

/**
 * Validate project name
 * - Required (non-empty after trim)
 * - 1-100 characters
 */
export const validateProjectName = (name: string): ValidationResult => {
    const trimmed = name.trim();

    if (!trimmed) {
        return {
            isValid: false,
            error: 'Project name is required',
        };
    }

    if (trimmed.length > 100) {
        return {
            isValid: false,
            error: 'Project name must be 100 characters or less',
        };
    }

    return { isValid: true };
};

/**
 * Validate committed delivery date
 * - Required
 * - Valid date format
 * - Not in the past (with option to allow past dates with warning)
 */
export const validateCommittedDate = (
    date: string,
    allowPast = false
): ValidationResult => {
    if (!date) {
        return {
            isValid: false,
            error: 'Committed delivery date is required',
        };
    }

    const selectedDate = parseLocalDate(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

    if (isNaN(selectedDate.getTime())) {
        return {
            isValid: false,
            error: 'Invalid date format',
        };
    }

    if (!allowPast && selectedDate < today) {
        return {
            isValid: false,
            error: 'Committed delivery date cannot be in the past',
        };
    }

    return { isValid: true };
};

/**
 * Validate entire project form
 */
export interface ProjectFormData {
    name: string;
    committed_delivery_date: string;
}

export interface ProjectFormErrors {
    name?: string;
    committed_delivery_date?: string;
}

export const validateProjectForm = (
    formData: ProjectFormData,
    allowPastDate = false
): { isValid: boolean; errors: ProjectFormErrors } => {
    const errors: ProjectFormErrors = {};

    const nameValidation = validateProjectName(formData.name);
    if (!nameValidation.isValid) {
        errors.name = nameValidation.error;
    }

    const dateValidation = validateCommittedDate(
        formData.committed_delivery_date,
        allowPastDate
    );
    if (!dateValidation.isValid) {
        errors.committed_delivery_date = dateValidation.error;
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

/**
 * Validate team member name
 * - Required (non-empty after trim)
 * - 1-100 characters
 */
export const validateTeamMemberName = (name: string): ValidationResult => {
    const trimmed = name.trim();

    if (!trimmed) {
        return {
            isValid: false,
            error: 'Team member name is required',
        };
    }

    if (trimmed.length > 100) {
        return {
            isValid: false,
            error: 'Team member name must be 100 characters or less',
        };
    }

    return { isValid: true };
};

/**
 * Validate team member role
 * - Required
 * - Must be 'developer' or 'qa'
 */
export const validateTeamMemberRole = (
    role: string
): ValidationResult => {
    if (!role) {
        return {
            isValid: false,
            error: 'Team member role is required',
        };
    }

    const validRoles = ['developer', 'qa'];
    if (!validRoles.includes(role.toLowerCase())) {
        return {
            isValid: false,
            error: 'Role must be either Developer or QA',
        };
    }

    return { isValid: true };
};

/**
 * Validate entire team member form
 */
export interface TeamMemberFormData {
    name: string;
    role: 'developer' | 'qa' | '';
}

export interface TeamMemberFormErrors {
    name?: string;
    role?: string;
}

export const validateTeamMemberForm = (
    formData: TeamMemberFormData
): { isValid: boolean; errors: TeamMemberFormErrors } => {
    const errors: TeamMemberFormErrors = {};

    const nameValidation = validateTeamMemberName(formData.name);
    if (!nameValidation.isValid) {
        errors.name = nameValidation.error;
    }

    const roleValidation = validateTeamMemberRole(formData.role);
    if (!roleValidation.isValid) {
        errors.role = roleValidation.error;
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

/**
 * Validate task name
 * - Required (non-empty after trim)
 * - 1-200 characters
 */
export const validateTaskName = (name: string): ValidationResult => {
    const trimmed = name.trim();

    if (!trimmed) {
        return {
            isValid: false,
            error: 'Task name is required',
        };
    }

    if (trimmed.length > 200) {
        return {
            isValid: false,
            error: 'Task name must be 200 characters or less',
        };
    }

    return { isValid: true };
};

/**
 * Validate task type
 * - Required
 * - Must be one of: design, development, integration, code_review, qa
 */
export const validateTaskType = (type: string): ValidationResult => {
    if (!type) {
        return {
            isValid: false,
            error: 'Task type is required',
        };
    }

    const validTypes = ['design', 'development', 'integration', 'code_review', 'qa'];
    if (!validTypes.includes(type)) {
        return {
            isValid: false,
            error: 'Invalid task type',
        };
    }

    return { isValid: true };
};

/**
 * Validate task size
 * - Required
 * - Must be a positive number
 * - Hours: 1-999
 * - Days: 1-365
 */
export const validateTaskSize = (
    sizeValue: number | string,
    sizeUnit: 'hours' | 'days' | ''
): ValidationResult => {
    if (!sizeUnit) {
        return {
            isValid: false,
            error: 'Size unit is required',
        };
    }

    const numValue = typeof sizeValue === 'string' ? parseFloat(sizeValue) : sizeValue;

    if (isNaN(numValue) || numValue <= 0) {
        return {
            isValid: false,
            error: 'Size must be a positive number',
        };
    }

    if (sizeUnit === 'hours' && (numValue < 1 || numValue > 999)) {
        return {
            isValid: false,
            error: 'Hours must be between 1 and 999',
        };
    }

    if (sizeUnit === 'days' && (numValue < 1 || numValue > 365)) {
        return {
            isValid: false,
            error: 'Days must be between 1 and 365',
        };
    }

    return { isValid: true };
};

/**
 * Validate date
 * - Required
 * - Valid date format
 */
export const validateDate = (date: string): ValidationResult => {
    if (!date) {
        return {
            isValid: false,
            error: 'Date is required',
        };
    }

    const selectedDate = new Date(date);
    if (isNaN(selectedDate.getTime())) {
        return {
            isValid: false,
            error: 'Invalid date format',
        };
    }

    return { isValid: true };
};

/**
 * Validate date range
 * - End date must be >= start date
 */
export const validateDateRange = (
    startDate: string,
    endDate: string
): ValidationResult => {
    if (!startDate || !endDate) {
        return { isValid: true }; // Let individual date validation handle required check
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) {
        return {
            isValid: false,
            error: 'End date must be on or after start date',
        };
    }

    return { isValid: true };
};

/**
 * Validate task status
 * - Required
 * - Must be one of: not_started, in_progress, completed, blocked
 */
export const validateTaskStatus = (status: string): ValidationResult => {
    if (!status) {
        return {
            isValid: false,
            error: 'Task status is required',
        };
    }

    const validStatuses = ['not_started', 'in_progress', 'completed', 'blocked'];
    if (!validStatuses.includes(status)) {
        return {
            isValid: false,
            error: 'Invalid task status',
        };
    }

    return { isValid: true };
};

/**
 * Task form data interface
 */
export interface TaskFormData {
    project_id: number;
    name: string;
    type: 'design' | 'development' | 'integration' | 'code_review' | 'qa' | '';
    size_unit: 'hours' | 'days' | '';
    size_value: number | string;
    start_date: string;
    end_date: string;
    status: 'not_started' | 'in_progress' | 'completed' | 'blocked' | '';
    assigned_member_id?: number | null;
}

/**
 * Task form errors interface
 */
export interface TaskFormErrors {
    project_id?: string;
    name?: string;
    type?: string;
    size_unit?: string;
    size_value?: string;
    start_date?: string;
    end_date?: string;
    status?: string;
    assigned_member_id?: string;
}

/**
 * Task type to allowed roles mapping
 * QA tasks require QA role
 * All other tasks (Design, Development, Integration, Code Review) require Developer role
 */
export const TASK_ROLE_COMPATIBILITY: Record<string, string[]> = {
    qa: ['qa'],
    design: ['developer'],
    development: ['developer'],
    integration: ['developer'],
    code_review: ['developer'],
};

/**
 * Validate task assignment based on role compatibility
 * - QA tasks can only be assigned to QA team members
 * - Design, Development, Integration, Code Review tasks can only be assigned to Developers
 * - Unassigning (null assigned_member_id) is always allowed
 */
export const validateTaskAssignment = (
    taskType: string,
    teamMemberRole: string | null | undefined
): ValidationResult => {
    // Unassigning is always valid
    if (!teamMemberRole) {
        return { isValid: true };
    }

    const normalizedType = taskType.toLowerCase();
    const normalizedRole = teamMemberRole.toLowerCase();

    // Check if task type is valid
    if (!TASK_ROLE_COMPATIBILITY[normalizedType]) {
        return {
            isValid: false,
            error: 'Invalid task type',
        };
    }

    const allowedRoles = TASK_ROLE_COMPATIBILITY[normalizedType];

    if (!allowedRoles.includes(normalizedRole)) {
        // Format task type for display (code_review -> Code Review)
        const displayTaskType = normalizedType
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        // Format role for display
        const displayRole = normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1);

        // Format required roles for display
        const displayRequiredRoles = allowedRoles
            .map((role) => role.charAt(0).toUpperCase() + role.slice(1))
            .join(' or ');

        return {
            isValid: false,
            error: `Cannot assign ${displayTaskType} task to ${displayRole}. Required role: ${displayRequiredRoles}`,
        };
    }

    return { isValid: true };
};

/**
 * Validate entire task form (with optional team member role for assignment validation)
 */
export const validateTaskForm = (
    formData: TaskFormData,
    teamMemberRole?: string | null
): { isValid: boolean; errors: TaskFormErrors } => {
    const errors: TaskFormErrors = {};

    // Validate project_id
    if (!formData.project_id || formData.project_id <= 0) {
        errors.project_id = 'Project is required';
    }

    // Validate name
    const nameValidation = validateTaskName(formData.name);
    if (!nameValidation.isValid) {
        errors.name = nameValidation.error;
    }

    // Validate type
    const typeValidation = validateTaskType(formData.type);
    if (!typeValidation.isValid) {
        errors.type = typeValidation.error;
    }

    // Validate size
    const sizeValidation = validateTaskSize(formData.size_value, formData.size_unit);
    if (!sizeValidation.isValid) {
        if (sizeValidation.error?.includes('unit')) {
            errors.size_unit = sizeValidation.error;
        } else {
            errors.size_value = sizeValidation.error;
        }
    }

    // Validate dates
    const startDateValidation = validateDate(formData.start_date);
    if (!startDateValidation.isValid) {
        errors.start_date = startDateValidation.error;
    }

    const endDateValidation = validateDate(formData.end_date);
    if (!endDateValidation.isValid) {
        errors.end_date = endDateValidation.error;
    }

    // Validate date range (only if both dates are valid)
    if (startDateValidation.isValid && endDateValidation.isValid) {
        const rangeValidation = validateDateRange(formData.start_date, formData.end_date);
        if (!rangeValidation.isValid) {
            errors.end_date = rangeValidation.error;
        }
    }

    // Validate status
    const statusValidation = validateTaskStatus(formData.status);
    if (!statusValidation.isValid) {
        errors.status = statusValidation.error;
    }

    // Validate task assignment (if team member is assigned)
    if (formData.assigned_member_id && teamMemberRole !== undefined) {
        const assignmentValidation = validateTaskAssignment(formData.type, teamMemberRole);
        if (!assignmentValidation.isValid) {
            errors.assigned_member_id = assignmentValidation.error;
        }
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

